/**
 * Merchant back-office route manifest (owned by the merchant surface).
 * Hand-built screens live here; generic CRUD pages for any registry model not
 * listed here still come from modules.config.js (see client/src/routes.jsx).
 *
 * { path, Element, label, icon, module, action, nav, group, foot }
 *   module/action  RBAC gate (route guard + nav visibility)
 *   nav            show in the sidebar;  group  sidebar section label
 *   foot           also show in the phone FootNav (max 4; "More" opens the full menu)
 *   models         registry models this screen replaces (their CRUD page is hidden)
 *   Element        receives { store } (the merchant resolved from the host)
 *
 * Every screen is backed by its module's permission (D4–D7); store settings stays
 * on core:view because each tab is gated by its own action (store:*, core:create).
 */
import { Navigate } from 'react-router-dom';
import MerchantDashboard from './pages/MerchantDashboard';
import Products from './pages/Products';
import InventoryImport from './pages/InventoryImport';
import UnitWizard from './pages/UnitWizard';
import ListingEditor from './pages/ListingEditor';
import Orders from './pages/Orders';
import OrderDetail from './pages/OrderDetail';
import Customers from './pages/Customers';
import Settlements from './pages/Settlements';
import StoreSettings from './pages/StoreSettings';
import Assistants from './pages/Assistants';
import Appearance from './pages/Appearance';
import Supply from './pages/Supply';
import Sourcing from './pages/Sourcing';
import Vendors from './pages/Vendors';
import VendorDetail from './pages/VendorDetail';

/** Pre-D37 catalog paths → where they live now. */
export const CATALOG_REDIRECTS = {
  '/inventory': '/products?tab=stock',
  '/inventory/new': '/add',
  '/listings': '/products?tab=listed',
  '/models': '/products?tab=models',
};
const redirectTo = (to) => {
  const Redirect = () => <Navigate to={to} replace />;
  Redirect.displayName = `Redirect(${to})`;
  return Redirect;
};

export const NAV_GROUPS = ['Overview', 'Catalog', 'Sales', 'Finance', 'Settings', 'Data'];

export const merchantRoutes = [
  // Overview
  { path: '/', Element: MerchantDashboard, label: 'Dashboard', icon: 'layout-dashboard', module: 'core', action: 'view', group: 'Overview', foot: true },

  // Catalog (D37): one "Add listing" wizard for any product type, one Products page
  // (Listed / In stock / Models tabs). Old paths redirect so links and bookmarks still work.
  { path: '/add', Element: UnitWizard, label: 'Add listing', icon: 'plus', module: 'inventory', action: 'create', group: 'Catalog' },
  { path: '/products', Element: Products, label: 'Products', icon: 'package', module: 'catalog', action: 'view', group: 'Catalog', models: ['InventoryUnit', 'ProductModel', 'Listing', 'Brand'], foot: true },
  // Vendor stores only (D57): the module is absent from the live registry otherwise.
  { path: '/supply', Element: Supply, label: 'Supply', icon: 'truck', module: 'supply', action: 'view', group: 'Catalog', models: ['SupplyOffer'] },
  { path: '/sourcing', Element: Sourcing, label: 'Sourcing', icon: 'share-2', module: 'dropship', action: 'view', group: 'Catalog', models: ['SourcedListing'] },
  // In-store vendors (B17, ADR 0006): approve sellers, review their products, set commission.
  { path: '/vendors', Element: Vendors, label: 'Vendors', icon: 'handshake', module: 'marketplace', action: 'view', group: 'Catalog', models: ['StoreVendor'] },
  { path: '/vendors/:id', Element: VendorDetail, label: 'Vendor', icon: 'handshake', module: 'marketplace', action: 'view', nav: false },
  { path: '/inventory/import', Element: InventoryImport, label: 'Import CSV', icon: 'upload', module: 'inventory', action: 'create', nav: false },
  { path: '/listings/:id', Element: ListingEditor, label: 'Listing', icon: 'tag', module: 'catalog', action: 'view', nav: false },
  ...Object.entries(CATALOG_REDIRECTS).map(([path, to]) => ({ path, Element: redirectTo(to), label: path, module: 'core', action: 'view', nav: false })),

  // Sales
  { path: '/orders', Element: Orders, label: 'Orders', icon: 'receipt', module: 'sales', action: 'view', group: 'Sales', foot: true },
  { path: '/orders/:id', Element: OrderDetail, label: 'Order', icon: 'receipt', module: 'sales', action: 'view', nav: false },
  { path: '/customers', Element: Customers, label: 'Customers & leads', icon: 'users', module: 'sales', action: 'view', group: 'Sales', models: ['Lead'] },

  // Finance
  { path: '/settlements', Element: Settlements, label: 'Settlements', short: 'Payouts', icon: 'wallet', module: 'finance', action: 'view', group: 'Finance', foot: true },

  // Settings (branding, locations, policies, staff, domains)
  { path: '/appearance', Element: Appearance, label: 'Appearance', icon: 'palette', module: 'store', action: 'appearance', group: 'Settings', models: ['StorefrontPage'] },
  { path: '/settings', Element: StoreSettings, label: 'Store settings', icon: 'settings', module: 'core', action: 'view', group: 'Settings', models: ['StaffInvite'] },
  // self: a page of the staff member's own things, open to everyone signed in (D48); no module.
  { path: '/assistants', self: true, Element: Assistants, label: 'AI assistants', icon: 'bot', group: 'Settings' },
];
